import type { ApoioOffline } from './parteDiariaOffline'
import { create } from 'zustand'
import { uuidv7 } from '@/lib/uuidv7'
import type { RejeicaoParteDiaria } from '@/core/db/entities'
import type {
  ContextoEppDto,
  ContextoMveDto,
  ContextoParteDiariaDto,
  MveParteDto,
  ServicoParteDto,
} from '@/types/parteDiariaDtos'
import {
  calcularEppMes,
  encadearInicios,
  hojeSP,
  somarDias,
  validarLinhaMve,
  validarPeriodoEpp,
  type ErroEpp,
  type ErroLinha,
  type FormularioMedicao,
  type ModoDia,
  type PapelLinha,
  type ServicoParte,
} from './parteDiariaRegras'

/**
 * Estado da parte diária em construção (um dia de MVE ou um período de EPP).
 *
 *  - `dt` nasce em hojeSP(); o operador pode trocar para ONTEM no passo 0
 *    (decisão 2026-09-21: ontem + hoje). Trocar a data zera contexto e linhas.
 *  - `contexto` é o snapshot de `GET /parte-diaria/contexto` (ou o cache);
 *    dele saem contrato, configs, obras, linhas do mês e o modo do dia.
 *  - Validação e prévia vêm de `parteDiariaRegras.ts` (cópia do backend).
 */

export type ModuloParteDiaria = 'MVE' | 'EPP'

export interface LinhaMveDraft {
  id: string
  obraId: number | null
  obraNome: string
  /** cdt_projeto.projeto_cmve da obra (null = desconhecido → trata como não CMVE). */
  projetoCmve: boolean | null
  servico: ServicoParteDto | null
  equipeId: number | null
  equipeNome: string | null
  /** Encarregado escolhido para filtrar as equipes (vínculo por projeto). Não é gravado no SIGA. */
  encarregadoId?: number | null
  encarregadoNome?: string | null
  ficha: string | null
  medicaoTermino: number | null
  chapa: string | null
  hInicio: string | null
  hFim: string | null
  observacao: string | null
}

export interface EppDraft {
  obraId: number | null
  obraNome: string
  servico: ServicoParteDto | null
  dtInicial: string | null
  dtFinal: string | null
  observacao: string | null
}

export interface ParteDiariaState {
  id: string
  dt: string
  modulo: ModuloParteDiaria | null
  mveId: number | null
  mvePrefixo: string | null
  mveDescricao: string | null
  mveTipo: 'M' | 'M+C' | 'E' | null
  mveTp: 'M' | 'V' | 'E' | null
  contexto: ContextoParteDiariaDto | null
  contextoFonte: 'live' | 'cache' | null
  /** Resumo do pacote offline + outbox local desta MVE hoje (ver parteDiariaOffline.ts). */
  apoio?: ApoioOffline | null
  linhas: LinhaMveDraft[]
  epp: EppDraft
  rejeicao: RejeicaoParteDiaria | null
}

interface ParteDiariaStore extends ParteDiariaState {
  setMve: (m: MveParteDto) => void
  setContexto: (ctx: ContextoParteDiariaDto | null, fonte: 'live' | 'cache' | null) => void
  setApoio: (a: ApoioOffline | null) => void
  /** Troca a data do lançamento (ontem/hoje). Início, modo e bloqueios mudam → zera o que depende dela. */
  setDt: (dt: string) => void
  addLinha: () => LinhaMveDraft
  updateLinha: (l: LinhaMveDraft) => void
  removeLinha: (id: string) => void
  setEpp: (patch: Partial<EppDraft>) => void
  limparRejeicao: () => void
  reset: () => void
  loadFromState: (s: ParteDiariaState) => void
}

const eppVazio = (): EppDraft => ({ obraId: null, obraNome: '', servico: null, dtInicial: null, dtFinal: null, observacao: null })

function estadoInicial(): ParteDiariaState {
  return {
    id: uuidv7(),
    dt: hojeSP(),
    modulo: null,
    mveId: null,
    mvePrefixo: null,
    mveDescricao: null,
    mveTipo: null,
    mveTp: null,
    contexto: null,
    contextoFonte: null,
    apoio: null,
    linhas: [],
    epp: eppVazio(),
    rejeicao: null,
  }
}

/** Rascunhos/outboxes de versões anteriores ganham os campos novos. */
export function migrarEstado(raw: unknown): ParteDiariaState {
  const r = (raw ?? {}) as Partial<ParteDiariaState>
  const base = estadoInicial()
  return {
    ...base,
    ...r,
    id: r.id ?? base.id,
    dt: r.dt ?? base.dt,
    linhas: Array.isArray(r.linhas) ? r.linhas : [],
    epp: { ...eppVazio(), ...(r.epp ?? {}) },
    rejeicao: r.rejeicao ?? null,
  }
}

export const useParteDiariaStore = create<ParteDiariaStore>((set, get) => ({
  ...estadoInicial(),
  setMve: (m) =>
    set({
      modulo: m.tipo === 'E' ? 'EPP' : 'MVE',
      mveId: m.id,
      mvePrefixo: m.prefixo,
      mveDescricao: [m.marca, m.modelo].filter(Boolean).join(' · ') || null,
      mveTipo: m.tipo,
      mveTp: m.mveTp,
      contexto: null,
      contextoFonte: null,
      apoio: null,
      linhas: [],
      epp: eppVazio(),
    }),
  setApoio: (a) => set({ apoio: a }),
  setDt: (dt) =>
    set((s) =>
      s.dt === dt ? s : { dt, contexto: null, contextoFonte: null, apoio: null, linhas: [], epp: { ...s.epp, dtInicial: null, dtFinal: null } },
    ),
  setContexto: (ctx, fonte) => {
    set({ contexto: ctx, contextoFonte: fonte })
    // Sugere obra/serviço/datas a partir do último período do mês (EPP)
    const s = get()
    if (ctx?.tipo === 'EPP' && s.epp.obraId == null && s.epp.dtInicial == null) {
      const ultima = ctx.linhasMes[ctx.linhasMes.length - 1]
      const obra = ultima ? ctx.obras.find((o) => o.id === ultima.obraId) : null
      set({
        epp: {
          ...s.epp,
          obraId: obra?.id ?? null,
          obraNome: obra ? `${obra.codigo}${obra.nome ? ` · ${obra.nome}` : ''}` : '',
          servico: ultima ? servicoDeLinha(ultima.servicoId, ultima.servicoNome, null, ultima.isento) : null,
          dtInicial: ultima && ultima.dtFinal < s.dt ? somarDias(ultima.dtFinal, 1) : ultima ? null : `${s.dt.slice(0, 7)}-01`,
          dtFinal: s.dt,
        },
      })
    }
  },
  addLinha: () => {
    const s = get()
    const l = novaLinhaComBase(s)
    set({ linhas: [...s.linhas, l] })
    return l
  },
  updateLinha: (l) => set((s) => ({ linhas: s.linhas.map((x) => (x.id === l.id ? l : x)) })),
  removeLinha: (id) => set((s) => ({ linhas: s.linhas.filter((x) => x.id !== id) })),
  setEpp: (patch) => set((s) => ({ epp: { ...s.epp, ...patch } })),
  limparRejeicao: () => set({ rejeicao: null }),
  reset: () => set(estadoInicial()),
  loadFromState: (s) => set({ ...migrarEstado(s) }),
}))

function servicoDeLinha(id: number | null, nome: string | null, grupoId: number | null, isento = false): ServicoParteDto | null {
  if (id == null) return null
  return { id, nome: nome ?? `Serviço ${id}`, isento, grupoId, grupoNome: null, inativo: false, coringa: false, tp: null }
}

/** Nova linha herdando obra/serviço/equipe/chapa/horas da linha anterior (ou da última do mês). */
export function novaLinhaComBase(s: ParteDiariaState): LinhaMveDraft {
  const anterior = s.linhas[s.linhas.length - 1]
  if (anterior) {
    return { ...anterior, id: uuidv7(), medicaoTermino: null, observacao: null, hInicio: anterior.hFim, hFim: null }
  }
  const ctx = s.contexto?.tipo === 'MVE' ? s.contexto : null
  const ultima = ctx ? [...ctx.dia.linhasHoje, ...ctx.linhasMes].sort((a, b) => (a.dt + a.sigaMveId).localeCompare(b.dt + b.sigaMveId)).pop() : null
  const obra = ultima?.obraId != null && ctx ? ctx.obras.find((o) => o.id === ultima.obraId) : null
  return {
    id: uuidv7(),
    obraId: obra?.id ?? null,
    obraNome: obra ? `${obra.codigo}${obra.nome ? ` · ${obra.nome}` : ''}` : '',
    projetoCmve: obra?.projetoCmve ?? null,
    servico: obra && ultima ? servicoDeLinha(ultima.servicoId, ultima.servicoNome, ultima.servicoGrupoId) : null,
    equipeId: obra ? (ultima?.equipeId ?? null) : null,
    equipeNome: null,
    ficha: null,
    medicaoTermino: null,
    chapa: ultima?.chapa && ultima.chapa !== '000000' ? ultima.chapa : null,
    hInicio: ultima?.hInicio ?? null,
    hFim: ultima?.hFim ?? null,
    observacao: null,
  }
}

// ============================================================================
//  Seletores
// ============================================================================

export const contextoMve = (s: ParteDiariaState): ContextoMveDto | null => (s.contexto?.tipo === 'MVE' ? s.contexto : null)
export const contextoEpp = (s: ParteDiariaState): ContextoEppDto | null => (s.contexto?.tipo === 'EPP' ? s.contexto : null)

/** Sem contexto (offline sem cache) assume dia novo; o servidor corrige com 409 corrigível. */
export function modoDia(s: ParteDiariaState): ModoDia {
  const c = contextoMve(s)
  if (c?.dia.modo === 'FRACOES') return 'FRACOES'
  // Já lancei esta MVE hoje neste aparelho e ainda não subiu: o próximo é fração.
  if ((s.apoio?.pendentesHoje ?? 0) > 0) return 'FRACOES'
  // Sem contexto (offline): vale o resumo do pacote.
  if (!c && (s.apoio?.linhasHoje ?? 0) > 0) return 'FRACOES'
  return 'DIA_NOVO'
}

export function formularioMedicao(s: ParteDiariaState): FormularioMedicao {
  return s.contexto?.contratoMes?.formularioMedicao ?? s.apoio?.formularioMedicao ?? 'MVE'
}

/** De onde veio o horímetro inicial mostrado — para a tela explicar ao operador. */
export function origemHorimetro(s: ParteDiariaState): 'siga' | 'cache' | 'resumo' | 'pendente' | null {
  if (formularioMedicao(s) === 'P') return null
  const c = contextoMve(s)
  const base = c ? c.dia.horimetroSugerido : (s.apoio?.horimetroSugerido ?? null)
  const pend = s.apoio?.ultimoTerminoPendente ?? null
  if (pend != null && (base == null || pend > base)) return 'pendente'
  if (base == null) return null
  return c ? (s.contextoFonte === 'live' ? 'siga' : 'cache') : 'resumo'
}

export const papelDaLinha = (s: ParteDiariaState, idx: number): PapelLinha =>
  modoDia(s) === 'DIA_NOVO' && idx === 0 ? 'PRINCIPAL' : 'FRACAO'

/** Início sugerido pelo servidor (null em formulário P ou sem contexto). */
export function horimetroInicial(s: ParteDiariaState): number | null {
  if (formularioMedicao(s) === 'P') return null
  const c = contextoMve(s)
  // Com contexto vale o dele; sem contexto (offline), o resumo do pacote — que
  // cobre TODAS as MVEs do chefe. Em ambos os casos, um lançamento desta MVE
  // ainda parado no outbox local empurra o início para o término dele.
  const base = c ? c.dia.horimetroSugerido : (s.apoio?.horimetroSugerido ?? null)
  const pend = s.apoio?.ultimoTerminoPendente ?? null
  if (base == null) return pend
  return pend != null && pend > base ? pend : base
}

export function iniciosDasLinhas(s: ParteDiariaState): Array<number | null> {
  const inicio0 = formularioMedicao(s) === 'P' ? 0 : horimetroInicial(s)
  return encadearInicios(inicio0, s.linhas.map((l) => l.medicaoTermino))
}

export function diferencaLinha(s: ParteDiariaState, idx: number): number | null {
  const l = s.linhas[idx]
  const inicio = iniciosDasLinhas(s)[idx]
  if (!l || l.medicaoTermino == null || inicio == null) return null
  return l.medicaoTermino - inicio
}

export const paraServicoParte = (sv: ServicoParteDto | null): ServicoParte | null =>
  sv ? { id: sv.id, nome: sv.nome, isento: sv.isento, grupoId: sv.grupoId, grupoNome: sv.grupoNome, inativo: sv.inativo } : null

export function errosDaLinha(s: ParteDiariaState, idx: number): ErroLinha[] {
  const l = s.linhas[idx]
  if (!l) return []
  const c = contextoMve(s)
  const inicio = iniciosDasLinhas(s)[idx] ?? 0
  return validarLinhaMve(
    {
      obraId: l.obraId,
      servicoExecutadoId: l.servico?.id ?? null,
      equipeId: l.equipeId,
      medicaoInicio: inicio,
      medicaoTermino: l.medicaoTermino,
      chapa: l.chapa,
      hInicio: l.hInicio,
      hFim: l.hFim,
    },
    {
      papel: papelDaLinha(s, idx),
      dia: Number(s.dt.slice(8, 10)),
      formularioMedicao: formularioMedicao(s),
      mveTp: s.mveTp ?? 'M',
      projetoCmve: l.projetoCmve ?? false,
      configs: c?.configs ?? { difKm: null, difHora: null, horaInicioFim: null, diasRetroativo: null },
      servico: paraServicoParte(l.servico),
      hodometroTransferenciaNoDia: c?.dia.hodometroTransferenciaNoDia ?? null,
      hodometroTransferenciaDiaSeguinte: idx === s.linhas.length - 1 ? (c?.dia.hodometroTransferenciaDiaSeguinte ?? null) : null,
    },
  )
}

export interface ErroLinhaIndexado {
  linhaId: string
  indice: number
  erros: ErroLinha[]
}

export function errosDoLancamentoMve(s: ParteDiariaState): ErroLinhaIndexado[] {
  return s.linhas
    .map((l, i) => ({ linhaId: l.id, indice: i, erros: errosDaLinha(s, i) }))
    .filter((x) => x.erros.length > 0)
}

export function errosEpp(s: ParteDiariaState): ErroEpp[] {
  const c = contextoEpp(s)
  return validarPeriodoEpp(
    { obraId: s.epp.obraId, servicoExecutadoId: s.epp.servico?.id ?? null, dtInicial: s.epp.dtInicial, dtFinal: s.epp.dtFinal },
    {
      hojeRef: s.dt,
      existentes: c?.linhasMes.map((l) => ({ dtInicial: l.dtInicial, dtFinal: l.dtFinal })) ?? [],
      admin: c?.sessao.admin ?? false,
      transferenciaDtUltima: c?.transferencia?.dt ?? null,
      ultimaDtFinalOutroChefe: c?.ultimaDtFinalOutroChefe ?? null,
      servico: paraServicoParte(s.epp.servico),
    },
  )
}

/** Prévia do valor do período (§4.7) com o Pup do contrato do mês; null sem contexto ou sem datas. */
export function previaValorEpp(s: ParteDiariaState): { valor: number; valorMes: number; dias: number } | null {
  const c = contextoEpp(s)
  if (!c || !c.contratoMes || !s.epp.dtInicial || !s.epp.dtFinal || s.epp.dtInicial > s.epp.dtFinal) return null
  const pup = c.contratoMes.pup ?? 0
  const linhas = [
    ...c.linhasMes.filter((l) => l.pup != null).map((l) => ({ id: l.sigaEppId, dtInicial: l.dtInicial, dtFinal: l.dtFinal, isento: l.isento, pup: l.pup ?? 0 })),
    { id: 'nova', dtInicial: s.epp.dtInicial, dtFinal: s.epp.dtFinal, isento: s.epp.servico?.isento ?? false, pup },
  ]
  const r = calcularEppMes(linhas, c.diasNoMes)
  const nova = r.linhas.find((l) => l.id === 'nova')
  return { valor: nova?.vr ?? 0, valorMes: r.valorRealizadoMes, dias: nova?.qr ?? 0 }
}

/** O dia/período está pronto para revisar e salvar? */
export function lancamentoCompleto(s: ParteDiariaState): boolean {
  if (s.mveId == null || s.modulo == null) return false
  if (s.modulo === 'MVE') return s.linhas.length > 0 && errosDoLancamentoMve(s).length === 0
  return errosEpp(s).length === 0
}

export function rotuloMve(s: ParteDiariaState): string {
  if (!s.mvePrefixo) return '(sem equipamento)'
  return s.mveDescricao ? `${s.mvePrefixo} · ${s.mveDescricao}` : s.mvePrefixo
}
